// SourcesOutputsPanel — desktop-web floating card that aggregates conversation
// files, project references and web citations (Inputs / Outputs / Sources, plus Work tasks,
// the Work browser, and video studio). Parent (ChatView) gates rendering:
// web, not XS, not compact, enough width, and at least one item or Work
// tasks. This component handles presentation + add-file / add-task actions.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Image,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Tooltip from '@/components/Tooltip';
import Switch from '@/components/Inputs/Switch';
import { taskScheduleHeadline } from '@/utils/tasks_catalog';
import WorkBrowserPreview, { WORK_BROWSER_PREVIEW_HEIGHT } from '@/components/Coworker/WorkBrowserPreview';
import {
  getFileIconByExtension,
  getFileIconByMimeType,
  getFileTypeColor,
  openMessageFile,
} from '@/constants/utils/files';
import { MAX_FILES_UPLOADED_IN_MESSAGE } from '@/constants/config';
import { useChatInputStore, useFileStore, useLightboxStore, useModalStore, useToastStore, useUIStore } from '@/store';
import { uploadFilesWithPlaceholders } from '@/lib/uploadFileWithPlaceholder';
import { useModalHelper } from '@/hooks/useModalHelper';
import { useTranslation } from '@/hooks/useTranslation';
import {
  OUTPUTS_RAIL_PREVIEW_LIMIT,
  getFileDisplayName,
  shortenFileNameKeepExt,
} from '@/constants/utils/sources_outputs';
import { hydrateOutputFileForOpen } from '@/constants/utils/conversation_outputs_download';
import { createFileOpenGuard } from '@/lib/fileOpenSession';
import { PANEL_WIDTH, PANEL_RIGHT_OFFSET } from './sourcesOutputsLayout';
import ProjectChangesSection from './ProjectChangesSection';

export const PANEL_TOP_OFFSET = 12;
// The card shares the right gutter with the conversation timeline rail, so a
// sources-only list stays compact. Work threads hide that rail and need room
// for the live browser thumbnail (see WORK_BROWSER_PREVIEW_HEIGHT); anything
// past the resulting height still scrolls inside the card.
const PANEL_MAX_HEIGHT = 280;
const PANEL_MIN_HEIGHT = 152;
const PANEL_BOTTOM_GUTTER = 12;
const PANEL_HEIGHT_SHARE = 0.45;
const PANEL_HEIGHT_SHARE_WITH_BROWSER = 0.62;
const PANEL_HEIGHT_SHARE_WITH_PINNED = 0.68;
// Title, section gap, and padding around the pinned thumbnail / studio tile.
const CLOUD_BROWSER_SECTION_CHROME = 48;
const CLOUD_BROWSER_SECTION_HEIGHT = WORK_BROWSER_PREVIEW_HEIGHT + CLOUD_BROWSER_SECTION_CHROME;
const VIDEO_STUDIO_TILE_HEIGHT = 52;
const VIDEO_STUDIO_SECTION_CHROME = 36;
const VIDEO_STUDIO_SECTION_HEIGHT = VIDEO_STUDIO_TILE_HEIGHT + VIDEO_STUDIO_SECTION_CHROME;
// Two-line task row (name + cadence) and the "Add task" action beneath it.
const TASK_ROW_HEIGHT = 44;
const ADD_TASK_ROW_HEIGHT = 34;
const TASKS_SECTION_CHROME = 40;
const TASKS_VISIBLE_ROWS = 3;
// Height of the fade that signals there is more of the list below.
const SCROLL_FADE_HEIGHT = 28;
// Fixed header holding the Hide control, and the pinned "Add sources" footer.
const HEADER_HEIGHT = 32;
const FOOTER_HEIGHT = 45;
// Reserved for the "Add sources" dropdown, which hangs below the card.
const ADD_MENU_HEIGHT = 88;
const ROW_ICON_SIZE = 22;
const EMPTY_FILES = [];
const EMPTY_TASKS = [];

function getBaseHostname(url) {
  if (!url || typeof url !== 'string') return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch (_) {
    return url
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0]
      .split('?')[0]
      .split('#')[0]
      .split(':')[0];
  }
}

function getFaviconUrl(domain, size = 64) {
  if (!domain) return null;
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=${size}`;
}

function isImageLike(file) {
  return file?.fileType === 'image'
    || file?.type === 'image'
    || (file?.mimeType || '').startsWith('image/');
}

function isVideoLike(file) {
  return file?.fileType === 'video'
    || file?.type === 'video'
    || (file?.mimeType || '').startsWith('video/');
}

function isAudioLike(file) {
  return file?.fileType === 'audio'
    || file?.type === 'audio'
    || (file?.mimeType || '').startsWith('audio/');
}

const FileThumb = React.memo(function FileThumb({ file, colors }) {
  const thumbUri = file?.thumbnailPath || (isImageLike(file) ? (file?.cdnPath || file?.url) : null);
  const iconName = file?.extension
    ? getFileIconByExtension(file.extension)
    : getFileIconByMimeType(file?.mimeType || file?.type);
  const tint = getFileTypeColor(file?.mimeType || file?.fileType || file?.type);

  if (thumbUri) {
    return (
      <Image
        source={{ uri: thumbUri }}
        style={styles.thumbImage}
        resizeMode="cover"
      />
    );
  }

  return (
    <View style={[styles.thumbIcon, { backgroundColor: tint }]}>
      <Icon
        name={isVideoLike(file) ? 'film' : isAudioLike(file) ? 'music' : iconName || 'file'}
        size={11}
        color="#fff"
      />
    </View>
  );
});

FileThumb.propTypes = {
  file: PropTypes.object,
  colors: PropTypes.object,
};

const PanelRow = React.memo(function PanelRow({
  icon,
  label,
  displayLabel,
  onPress,
  trailing,
  muted,
}) {
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);

  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        styles.row,
        hovered && { backgroundColor: colors.solid_color_tertiary },
        Platform.OS === 'web' && { cursor: onPress ? 'pointer' : 'default' },
      ]}
      accessibilityRole={onPress ? 'button' : undefined}
      accessibilityLabel={label}
      {...(Platform.OS === 'web' ? { title: label } : {})}
    >
      <View style={styles.rowIconSlot}>{icon}</View>
      <Text
        size="s"
        color={muted ? 'secondary' : 'primary'}
        numberOfLines={1}
        style={styles.rowLabel}
      >
        {displayLabel || label}
      </Text>
      {trailing}
    </Pressable>
  );
});

PanelRow.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string.isRequired,
  displayLabel: PropTypes.string,
  onPress: PropTypes.func,
  trailing: PropTypes.node,
  muted: PropTypes.bool,
};

// One scheduled task (InferenceTask) that runs inside this Work thread.
// Tap → the shared CreateTaskModal (edit / run now / delete); switch → pause.
const TaskRow = React.memo(function TaskRow({
  task,
  onEdit,
  onSetEnabled,
  onDelete,
  editLabel,
  deleteLabel,
  pausedLabel,
  enabledLabel,
}) {
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);
  const [toggling, setToggling] = useState(false);
  const enabled = task?.flags?.isEnabled !== false;
  const name = task?.name || '';
  const cadence = taskScheduleHeadline(task);

  const handleToggle = useCallback(async (next) => {
    if (toggling) return;
    setToggling(true);
    try { await onSetEnabled?.(task, next); } finally { setToggling(false); }
  }, [onSetEnabled, task, toggling]);

  return (
    <View
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={[
        styles.row,
        styles.taskRow,
        hovered && { backgroundColor: colors.solid_color_tertiary },
      ]}
    >
      <Pressable
        onPress={() => onEdit?.(task)}
        style={[styles.taskMain, Platform.OS === 'web' && { cursor: 'pointer' }]}
        accessibilityRole="button"
        accessibilityLabel={`${editLabel}: ${name}`}
        testID={task?.id ? `work-task-${task.id}` : undefined}
      >
        <Text
          size="s"
          color={enabled ? 'primary' : 'secondary'}
          numberOfLines={1}
          style={styles.rowLabel}
        >
          {name}
        </Text>
        <Text size="xs" color="tertiary" numberOfLines={1} style={styles.rowLabel}>
          {enabled ? cadence : `${pausedLabel} · ${cadence}`}
        </Text>
      </Pressable>
      {onDelete ? (
        <Pressable
          onPress={() => onDelete(task)}
          hitSlop={6}
          style={[styles.taskDelete, Platform.OS === 'web' && { cursor: 'pointer' }]}
          accessibilityRole="button"
          accessibilityLabel={`${deleteLabel}: ${name}`}
        >
          <Icon name="xmark" size={10} color={colors.text_color_tertiary} />
        </Pressable>
      ) : null}
      <View style={styles.taskSwitch}>
        <Switch
          value={enabled}
          onValueChange={handleToggle}
          disabled={toggling || !onSetEnabled}
          accessibilityLabel={`${enabledLabel}: ${name}`}
        />
      </View>
    </View>
  );
});

TaskRow.propTypes = {
  task: PropTypes.object,
  onEdit: PropTypes.func,
  onSetEnabled: PropTypes.func,
  onDelete: PropTypes.func,
  editLabel: PropTypes.string,
  deleteLabel: PropTypes.string,
  pausedLabel: PropTypes.string,
  enabledLabel: PropTypes.string,
};

const SourcesOutputsPanel = ({
  conversationId,
  taskId,
  hasProjectFolder = false,
  inputs = EMPTY_FILES,
  outputs = EMPTY_FILES,
  sources = EMPTY_FILES,
  collectionId,
  availableHeight = 0,
  onOccupiedHeightChange,
  cloudBrowser = null,
  videoStudio = null,
  tasks = EMPTY_TASKS,
  showTasks = false,
  canAddTask = true,
  onAddTask,
  onEditTask,
  onSetTaskEnabled,
  onDeleteTask,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const rootRef = useRef(null);
  const openRequest = useRef(0);
  useLayoutEffect(() => () => { openRequest.current += 1; }, [conversationId, taskId]);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [sourceActionBottom, setSourceActionBottom] = useState(null);
  // Session-local tuck-away: keeps the panel easy to restore without flipping
  // the Appearance preference that permanently disables it.
  const [collapsed, setCollapsed] = useState(false);
  const cardBackground = colors.solid_color_block || colors.solid_color_secondary;

  const browserSectionHeight = cloudBrowser ? CLOUD_BROWSER_SECTION_HEIGHT : 0;
  const videoStudioSectionHeight = videoStudio ? VIDEO_STUDIO_SECTION_HEIGHT : 0;
  const tasksSectionHeight = showTasks
    ? TASKS_SECTION_CHROME
      + Math.min(tasks.length, TASKS_VISIBLE_ROWS) * TASK_ROW_HEIGHT
      + ADD_TASK_ROW_HEIGHT
    : 0;
  const pinnedSectionHeight = browserSectionHeight + videoStudioSectionHeight + tasksSectionHeight;
  const panelMaxHeight = useMemo(() => {
    const hardMax = PANEL_MAX_HEIGHT + pinnedSectionHeight + (hasProjectFolder ? 180 : 0);
    const floor = pinnedSectionHeight
      ? HEADER_HEIGHT + FOOTER_HEIGHT + pinnedSectionHeight + 56
      : PANEL_MIN_HEIGHT;
    const share = (browserSectionHeight && videoStudioSectionHeight)
      ? PANEL_HEIGHT_SHARE_WITH_PINNED
      : ((pinnedSectionHeight || hasProjectFolder) ? PANEL_HEIGHT_SHARE_WITH_BROWSER : PANEL_HEIGHT_SHARE);
    if (!availableHeight) return hardMax;
    const usable = Math.max(0, availableHeight - PANEL_TOP_OFFSET - PANEL_BOTTOM_GUTTER);
    const cappedFloor = usable ? Math.min(floor, usable) : floor;
    return Math.max(
      cappedFloor,
      Math.min(hardMax, Math.round(usable * share) || cappedFloor)
    );
  }, [availableHeight, browserSectionHeight, pinnedSectionHeight, videoStudioSectionHeight, hasProjectFolder]);
  // AppKit's intrinsic ScrollView size and Yoga's maxHeight can feed back
  // into each other as Git rows update. The Mac card gets a viewport-sized
  // frame; only its inner list scrolls. Its size never depends on row count.
  const nativePanelFrame = Platform.OS === 'macos' ? { height: panelMaxHeight } : null;
  const scrollMaxHeight = Math.max(0, panelMaxHeight - HEADER_HEIGHT - FOOTER_HEIGHT - pinnedSectionHeight - (Platform.OS === 'macos' ? 2 : 0));
  const nativeScrollFrame = Platform.OS === 'macos'
    ? { height: scrollMaxHeight, flexGrow: 0, flexShrink: 0 }
    : null;

  // Bottom fade doubles as the "there is more below" hint, since macOS overlay
  // scrollbars stay invisible until the user already scrolls.
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollMetricsRef = useRef({ content: 0, viewport: 0, offset: 0 });
  const syncScrollAffordance = useCallback(() => {
    const { content, viewport, offset } = scrollMetricsRef.current;
    const more = content - viewport - offset > 8;
    setCanScrollDown((prev) => (prev === more ? prev : more));
    const moved = offset > 4;
    setIsScrolled((prev) => (prev === moved ? prev : moved));
  }, []);
  const handleScroll = useCallback((event) => {
    scrollMetricsRef.current.offset = event?.nativeEvent?.contentOffset?.y ?? 0;
    syncScrollAffordance();
  }, [syncScrollAffordance]);
  const handleContentSizeChange = useCallback((_width, height) => {
    scrollMetricsRef.current.content = height ?? 0;
    syncScrollAffordance();
  }, [syncScrollAffordance]);
  const handleScrollViewLayout = useCallback((event) => {
    scrollMetricsRef.current.viewport = event?.nativeEvent?.layout?.height ?? 0;
    syncScrollAffordance();
  }, [syncScrollAffordance]);

  // Report the vertical band this card occupies so the parent can keep the
  // timeline rail's hover panel out from under it. Reported from the events
  // that change it rather than from an effect, so the parent re-renders once.
  const cardHeightRef = useRef(0);
  const reportOccupiedHeight = useCallback((height) => {
    onOccupiedHeightChange?.(height);
  }, [onOccupiedHeightChange]);

  const handleCardLayout = useCallback((event) => {
    const height = event?.nativeEvent?.layout?.height;
    if (typeof height !== 'number' || Math.abs(cardHeightRef.current - height) < 1) return;
    cardHeightRef.current = height;
    reportOccupiedHeight(height + (addMenuOpen ? ADD_MENU_HEIGHT : 0));
  }, [addMenuOpen, reportOccupiedHeight]);

  const closeAddMenu = useCallback(() => {
    setAddMenuOpen(false);
    reportOccupiedHeight(cardHeightRef.current);
  }, [reportOccupiedHeight]);

  const toggleAddMenu = useCallback(() => {
    const next = !addMenuOpen;
    setAddMenuOpen(next);
    reportOccupiedHeight(cardHeightRef.current + (next ? ADD_MENU_HEIGHT : 0));
  }, [addMenuOpen, reportOccupiedHeight]);

  // The collapsed chip is tiny and tucked in the corner, so it reserves nothing.
  const handleCollapse = useCallback(() => {
    setAddMenuOpen(false);
    setCollapsed(true);
    // Cleared so re-expanding to the same height still reports a fresh value.
    cardHeightRef.current = 0;
    reportOccupiedHeight(0);
  }, [reportOccupiedHeight]);

  // The menu escapes the card, so nothing else in the chat can absorb the
  // "click elsewhere to dismiss" gesture — listen on the document instead of
  // laying a full-screen overlay over the conversation.
  useEffect(() => {
    if (!addMenuOpen || Platform.OS !== 'web' || typeof document === 'undefined') return undefined;

    const handlePointerDown = (event) => {
      const node = rootRef.current;
      if (node?.contains?.(event.target)) return;
      closeAddMenu();
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeAddMenu();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [addMenuOpen, closeAddMenu]);

  const requestScrollToMessage = useUIStore((state) => state.requestScrollToMessage);
  const setHighlightedMessageId = useUIStore((state) => state.setHighlightedMessageId);
  const { openLightbox } = useLightboxStore((state) => state.actions);
  const { openModal } = useModalStore((state) => state.actions);
  const { addToast } = useToastStore((state) => state.actions);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();

  const setContext = useChatInputStore((state) => state.setContext);
  const addFile = useChatInputStore((state) => state.addFile);
  const setFiles = useChatInputStore((state) => state.setFiles);
  const composerFiles = useChatInputStore(
    (state) => state.inputStates?.[conversationId]?.files ?? EMPTY_FILES
  );

  const { uploadFile, isFileAllowed, fetchAllowedFileTypes, hasCreditsForFileUpload } = useFileStore(
    (state) => state.actions
  );

  const scrollToMessage = useCallback((messageId) => {
    if (!messageId || !conversationId) return;
    requestScrollToMessage({ conversationId, messageId, viewPosition: 0.2 });
    setHighlightedMessageId(messageId);
  }, [conversationId, requestScrollToMessage, setHighlightedMessageId]);

  const handleFilePress = useCallback(async (entry) => {
    const file = entry?.file;
    if (!file) return;
    // Don't scroll the transcript to the source message. The preview covers
    // the chat, so that jump is only felt after close and loses the reader's place.

    if (entry.isPending || file.isUploading) return;

    const request = ++openRequest.current;
    const shouldOpen = createFileOpenGuard(() => request === openRequest.current);
    try {
      const openable = await hydrateOutputFileForOpen(file, { taskId, shouldOpen });
      if (!shouldOpen()) return;
      await openMessageFile(openable, {
        shouldOpen,
        conversationId,
        taskId,
        onOpenLightbox: ({ imageUrl, fileId, openToEditor, withInpainting, withChatInput }) => {
          openLightbox({
            imageUrl,
            fileId,
            conversationId,
            openToEditor,
            withInpainting,
            withChatInput,
          });
        },
        openToEditor: false,
        withInpainting: true,
        withChatInput: true,
      });
    } catch (error) {
      if (shouldOpen()) addToast(error?.message || t('media.downloadFailed'), 'error');
    }
  }, [addToast, openLightbox, conversationId, t, taskId]);

  const openOutputsModal = useCallback(() => {
    if (!outputs.length) return;
    openModal('ConversationOutputsModal', {
      outputs,
      conversationId,
      taskId,
    });
  }, [conversationId, openModal, outputs, taskId]);

  const handleLinkPress = useCallback((entry) => {
    const url = entry?.url;
    if (!url) return;
    if (entry.messageId) scrollToMessage(entry.messageId);

    const absolute = (url.startsWith('http://') || url.startsWith('https://')) ? url : `https://${url}`;
    showConfirmationModal({
      title: t('chatDetails.externalLink'),
      message: t('chatDetails.externalLinkMessage'),
      itemName: absolute,
      confirmText: t('chatDetails.openLink'),
      cancelText: t('common.cancel'),
      onConfirm: () => {
        if (Platform.OS === 'web') {
          window.open(absolute, '_blank', 'noopener,noreferrer');
        } else {
          Linking.openURL(absolute);
        }
      },
    });
  }, [scrollToMessage, showConfirmationModal, t]);

  const replaceTempFile = useCallback((tempId, uploadedFile) => {
    setContext(conversationId);
    const state = useChatInputStore.getState();
    const currentFiles = state.inputStates?.[conversationId]?.files || [];
    const getId = (f) => (typeof f === 'object' ? f?.id : f);
    const tempIndex = currentFiles.findIndex((f) => getId(f) === tempId);
    if (tempIndex === -1) {
      addFile(uploadedFile);
      return;
    }
    const duplicate = currentFiles.some((f, i) => i !== tempIndex && getId(f) === uploadedFile?.id);
    const next = [...currentFiles];
    if (duplicate) next.splice(tempIndex, 1);
    else next[tempIndex] = uploadedFile;
    setFiles(next);
  }, [conversationId, setContext, addFile, setFiles]);

  const processPickedFiles = useCallback(async (pickedFiles) => {
    if (!conversationId) return;
    setContext(conversationId);

    const currentFiles = useChatInputStore.getState().inputStates?.[conversationId]?.files || [];
    const remainingSlots = Math.max(0, (MAX_FILES_UPLOADED_IN_MESSAGE || 10) - currentFiles.length);
    if (remainingSlots <= 0) {
      addToast(t('chatInput.maxFilesPerMessage').replace('%MAX%', MAX_FILES_UPLOADED_IN_MESSAGE), 'error');
      return;
    }

    try { await fetchAllowedFileTypes(); } catch (_) { /* allowlist optional — server validates */ }
    const { hasCredits, message: creditsMessage } = hasCreditsForFileUpload();
    if (!hasCredits) {
      addToast(creditsMessage, 'error');
      return;
    }

    await uploadFilesWithPlaceholders(Array.from(pickedFiles).slice(0, remainingSlots), {
      collectionId,
      uploadFile,
      isFileAllowed,
      addToast,
      onAddUploadingFile: addFile,
      onAddFile: (uploadedFile, tempId, uploadMeta) => {
        if (uploadMeta?.placeholderReplaced) return;
        replaceTempFile(tempId, { ...uploadedFile, source: uploadedFile.source || 'upload' });
      },
      t,
      extraTempFields: (file) => ({ previewFile: file, source: 'upload' }),
    });
  }, [
    conversationId,
    setContext,
    addToast,
    t,
    fetchAllowedFileTypes,
    hasCreditsForFileUpload,
    isFileAllowed,
    addFile,
    uploadFile,
    collectionId,
    replaceTempFile,
  ]);

  const handleUploadClick = useCallback(() => {
    closeAddMenu();
    if (composerFiles.length >= MAX_FILES_UPLOADED_IN_MESSAGE) {
      addToast(t('chatInput.maxFilesPerMessage').replace('%MAX%', MAX_FILES_UPLOADED_IN_MESSAGE), 'error');
      return;
    }
    fileInputRef.current?.click();
  }, [closeAddMenu, composerFiles.length, addToast, t]);

  const handleLibraryClick = useCallback(() => {
    closeAddMenu();
    if (composerFiles.length >= MAX_FILES_UPLOADED_IN_MESSAGE) {
      addToast(t('chatInput.maxFilesPerMessage').replace('%MAX%', MAX_FILES_UPLOADED_IN_MESSAGE), 'error');
      return;
    }
    setContext(conversationId);
    openModal('FilesModal', {
      onSelect: (picked) => {
        setContext(conversationId);
        const items = Array.isArray(picked) ? picked : [picked];
        items.filter(Boolean).forEach((f) => addFile(f));
      },
    });
  }, [closeAddMenu, composerFiles.length, addToast, t, setContext, conversationId, openModal, addFile]);

  const handleFileInputChange = useCallback(async (event) => {
    const selected = Array.from(event?.target?.files || []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!selected.length) return;
    await processPickedFiles(selected);
  }, [processPickedFiles]);

  const hasInputs = inputs.length > 0;
  const hasOutputs = outputs.length > 0;
  const hasSources = sources.length > 0;
  const hasTasks = showTasks;
  const studioTitle = t(`chat.sourcesOutputs.${videoStudio?.kind === 'image' ? 'imageStudio' : 'videoStudio'}`);
  const studioAction = t(`chat.sourcesOutputs.${videoStudio?.kind === 'image' ? 'openImageStudio' : 'openVideoStudio'}`);
  const itemCount = inputs.length + outputs.length + sources.length + (hasTasks ? tasks.length : 0);
  const collapsedParts = [
    hasProjectFolder && 'Project',
    hasInputs && t('chat.sourcesOutputs.inputs'),
    hasOutputs && t('chat.sourcesOutputs.outputs'),
    hasSources && t('chat.sourcesOutputs.sources'),
    hasTasks && t('chat.sourcesOutputs.tasks'),
  ].filter(Boolean);
  const collapsedSummary = collapsedParts.length === 0
    ? (videoStudio
      ? studioTitle
      : t('chat.sourcesOutputs.cloudBrowser'))
    : `${collapsedParts.join(' + ')} · ${itemCount}`;
  const panelLabel = [
    hasProjectFolder && 'Project',
    hasInputs && t('chat.sourcesOutputs.inputs'),
    hasOutputs && t('chat.sourcesOutputs.outputs'),
    hasSources && t('chat.sourcesOutputs.sources'),
    hasTasks && t('chat.sourcesOutputs.tasks'),
    videoStudio && studioTitle,
    cloudBrowser && t('chat.sourcesOutputs.cloudBrowser'),
  ].filter(Boolean).join(', ');

  const inputRows = useMemo(() => (
    inputs.map((entry) => (
      <PanelRow
        key={entry.id}
        label={getFileDisplayName(entry.file)}
        displayLabel={shortenFileNameKeepExt(getFileDisplayName(entry.file))}
        onPress={() => handleFilePress(entry)}
        muted={!!entry.isPending || !!entry.file?.isUploading}
        icon={<FileThumb file={entry.file} colors={colors} />}
      />
    ))
  ), [inputs, handleFilePress, colors]);

  const hiddenOutputCount = Math.max(0, outputs.length - OUTPUTS_RAIL_PREVIEW_LIMIT);
  const outputRows = useMemo(() => (
    outputs.slice(0, OUTPUTS_RAIL_PREVIEW_LIMIT).map((entry) => (
      <PanelRow
        key={entry.id}
        label={getFileDisplayName(entry.file)}
        displayLabel={shortenFileNameKeepExt(getFileDisplayName(entry.file))}
        onPress={() => handleFilePress(entry)}
        icon={<FileThumb file={entry.file} colors={colors} />}
      />
    ))
  ), [outputs, handleFilePress, colors]);

  const sourceRows = useMemo(() => (
    sources.map((entry) => {
      if (entry.kind === 'project_file') {
        return (
          <Tooltip key={entry.id} label={entry.path}>
            <PanelRow
              label={`Open source file ${entry.path}`}
              displayLabel={entry.path.split('/').pop()}
              onPress={() => openModal('ProjectFilePreviewModal', { taskId, path: entry.path })}
              icon={(
                <View style={[styles.thumbIcon, { backgroundColor: colors.solid_color_tertiary }]}>
                  <Icon name="file-code" size={11} color={colors.text_color_secondary} />
                </View>
              )}
            />
          </Tooltip>
        );
      }
      const domain = entry.hostname || getBaseHostname(entry.url);
      const favicon = getFaviconUrl(domain);
      return (
        <PanelRow
          key={entry.id}
          label={entry.title || domain || entry.url}
          onPress={() => handleLinkPress(entry)}
          icon={favicon ? (
            <Image source={{ uri: favicon }} style={styles.favicon} />
          ) : (
            <View style={[styles.thumbIcon, { backgroundColor: colors.solid_color_tertiary }]}>
              <Icon name="link" size={11} color={colors.text_color_secondary} />
            </View>
          )}
          trailing={(
            <Icon name="arrow-up-right-from-square" size={10} color={colors.text_color_tertiary} />
          )}
        />
      );
    })
  ), [sources, handleLinkPress, colors, openModal, taskId]);

  const taskRows = useMemo(() => (
    tasks.map((task) => (
      <TaskRow
        key={task.id}
        task={task}
        onEdit={onEditTask}
        onSetEnabled={onSetTaskEnabled}
        onDelete={onDeleteTask}
        editLabel={t('chat.sourcesOutputs.editTask')}
        deleteLabel={t('chat.sourcesOutputs.deleteTask')}
        pausedLabel={t('tasks.pausedStatus')}
        enabledLabel={t('tasks.enabledStatus')}
      />
    ))
  ), [onDeleteTask, onEditTask, onSetTaskEnabled, t, tasks]);

  if (!hasInputs && !hasOutputs && !hasSources && !hasTasks && !cloudBrowser && !videoStudio && !hasProjectFolder) return null;

  if (collapsed) {
    return (
      <View style={styles.collapsedRoot} pointerEvents="box-none">
        <Pressable
          onPress={() => setCollapsed(false)}
          style={({ hovered }) => [
            styles.collapsedChip,
            hovered && { backgroundColor: colors.solid_color_tertiary },
            Platform.OS === 'web' && { cursor: 'pointer' },
          ]}
          accessibilityRole="button"
          accessibilityLabel={t('chat.sourcesOutputs.expand')}
        >
          <Icon name="layer-group" size={10} color={colors.text_color_tertiary} />
          <Text size="xs" color="tertiary" numberOfLines={1}>
            {collapsedSummary}
          </Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View ref={rootRef} style={styles.root} pointerEvents="box-none">
      <View
        testID="sources-panel-card"
        onLayout={handleCardLayout}
        style={[
          styles.panel,
          {
            backgroundColor: cardBackground,
            borderColor: colors.border_color_secondary,
            maxHeight: panelMaxHeight,
          },
          nativePanelFrame,
        ]}
        accessibilityRole="complementary"
        accessibilityLabel={panelLabel}
      >
        {/* Fixed header: keeps Hide out of the scroll area so rows never slide
            underneath it. Its divider only appears once the list has moved. */}
        <View
          style={[
            styles.header,
            isScrolled && {
              borderBottomWidth: StyleSheet.hairlineWidth,
              borderBottomColor: colors.border_color_secondary,
            },
          ]}
        >
          <Tooltip label={t('chat.sourcesOutputs.collapseTooltip')} side="bottom" align="end" delayDuration={300}>
            <Pressable
              onPress={handleCollapse}
              hitSlop={6}
              style={({ hovered }) => [
                styles.hideButton,
                hovered && { backgroundColor: colors.solid_color_tertiary },
                Platform.OS === 'web' && { cursor: 'pointer' },
              ]}
              accessibilityRole="button"
              accessibilityLabel={t('chat.sourcesOutputs.collapseTooltip')}
            >
              <Text size="xs" color="tertiary">
                {t('chat.sourcesOutputs.collapse')}
              </Text>
              <Icon name="arrow-right-to-line" size={10} color={colors.text_color_tertiary} />
            </Pressable>
          </Tooltip>
        </View>
        {(hasInputs || hasOutputs || hasSources || showTasks || hasProjectFolder) && (
        <ScrollView
          testID="sources-panel-scroll"
          // RN Web only scrolls when the scroll node itself is height-bounded;
          // maxHeight on an ancestor plus overflow:hidden clips the list
          // without ever giving it a scrollbar. The cloud-browser tile is
          // pinned below this scroller so the thumbnail is never clipped.
          style={[styles.scroll, { maxHeight: scrollMaxHeight }, nativeScrollFrame]}
          contentContainerStyle={[
            styles.scrollContent,
            pinnedSectionHeight ? { paddingBottom: 0 } : null,
          ]}
          showsVerticalScrollIndicator
          nestedScrollEnabled
          onLayout={handleScrollViewLayout}
          onContentSizeChange={handleContentSizeChange}
          onScroll={handleScroll}
          scrollEventThrottle={32}
        >
          {hasProjectFolder && <ProjectChangesSection taskId={taskId} />}
          {hasInputs && (
            <View style={styles.section}>
              <Text size="xs" color="secondary" style={styles.sectionTitle}>
                {`${t('chat.sourcesOutputs.inputs')} · ${inputs.length}`}
              </Text>
              <View style={styles.sectionBody}>{inputRows}</View>
            </View>
          )}

          {hasOutputs && (
            <View style={[styles.section, hasInputs && styles.sectionSpaced]}>
              <Pressable
                onPress={openOutputsModal}
                accessibilityRole="button"
                accessibilityLabel={t('chat.sourcesOutputs.viewAllOutputs')}
                style={({ hovered }) => [
                  styles.sectionTitleButton,
                  hovered && { backgroundColor: colors.solid_color_tertiary },
                  Platform.OS === 'web' && { cursor: 'pointer' },
                ]}
              >
                <Text size="xs" color="secondary" style={styles.sectionTitleText}>
                  {`${t('chat.sourcesOutputs.outputs')} · ${outputs.length}`}
                </Text>
                <Icon name="arrow-up-right-from-square" size={9} color={colors.text_color_tertiary} />
              </Pressable>
              <View style={styles.sectionBody}>
                {outputRows}
                {hiddenOutputCount > 0 && (
                  <Pressable
                    onPress={openOutputsModal}
                    accessibilityRole="button"
                    accessibilityLabel={t('chat.sourcesOutputs.viewAllOutputsCount', { count: outputs.length })}
                    style={({ hovered }) => [
                      styles.row,
                      hovered && { backgroundColor: colors.solid_color_tertiary },
                      Platform.OS === 'web' && { cursor: 'pointer' },
                    ]}
                  >
                    <View style={styles.rowIconSlot}>
                      <View style={[styles.thumbIcon, { backgroundColor: colors.solid_color_tertiary }]}>
                        <Icon name="layer-group" size={11} color={colors.text_color_secondary} />
                      </View>
                    </View>
                    <Text size="s" color="secondary" numberOfLines={1} style={styles.rowLabel}>
                      {t('chat.sourcesOutputs.viewAllOutputsCount', { count: outputs.length })}
                    </Text>
                  </Pressable>
                )}
              </View>
            </View>
          )}

          {/* Work threads show the Sources heading from the first message so
              the box reads as "Sources" before anything has been cited. */}
          {(hasSources || showTasks) && (
            <View style={[styles.section, (hasInputs || hasOutputs) && styles.sectionSpaced]}>
              <Text size="xs" color="secondary" style={styles.sectionTitle}>
                {`${t('chat.sourcesOutputs.sources')} · ${sources.length}`}
              </Text>
              <View style={styles.sectionBody}>
                {hasSources ? sourceRows : (
                  <Text size="s" color="tertiary" style={styles.emptyHint}>
                    {t('chat.sourcesOutputs.noSourcesYet')}
                  </Text>
                )}
              </View>
            </View>
          )}
        </ScrollView>
        )}
        {canScrollDown && (
          <View
            style={[
              styles.scrollFade,
              pinnedSectionHeight ? { bottom: FOOTER_HEIGHT + pinnedSectionHeight } : null,
            ]}
            pointerEvents="none"
          >
            <LinearGradient
              colors={['transparent', cardBackground]}
              style={StyleSheet.absoluteFill}
            />
          </View>
        )}
        <View
          onLayout={({ nativeEvent }) => setSourceActionBottom(nativeEvent.layout.y + nativeEvent.layout.height)}
          style={[styles.footer, { borderTopColor: colors.border_color_secondary }]}
        >
          <Pressable
            onPress={toggleAddMenu}
            style={({ hovered }) => [
              styles.addButton,
              hovered && { backgroundColor: colors.solid_color_tertiary },
              Platform.OS === 'web' && { cursor: 'pointer' },
            ]}
            accessibilityRole="button"
            accessibilityState={{ expanded: addMenuOpen }}
            accessibilityLabel={t('chat.sourcesOutputs.addSources')}
          >
            <Icon name="plus" size={11} color={colors.text_color_secondary} />
            <Text size="s" color="secondary">
              {t('chat.sourcesOutputs.addSources')}
            </Text>
          </Pressable>
        </View>
        {/* Scheduled tasks that run inside this Work thread. Pinned under
            Sources so a long citation list never hides them. */}
        {hasTasks && (
          <View
            style={[
              styles.section,
              styles.tasksSection,
              (hasInputs || hasOutputs || hasSources || showTasks) && styles.sectionSpaced,
            ]}
          >
            <Text size="xs" color="secondary" style={styles.sectionTitle}>
              {`${t('chat.sourcesOutputs.tasks')} · ${tasks.length}`}
            </Text>
            <View style={styles.sectionBody}>
              {tasks.length > TASKS_VISIBLE_ROWS ? (
                <View style={[styles.taskList, { maxHeight: TASKS_VISIBLE_ROWS * TASK_ROW_HEIGHT }]}>
                  {taskRows}
                </View>
              ) : taskRows}
              <Pressable
                onPress={onAddTask}
                disabled={!canAddTask}
                accessibilityState={{ disabled: !canAddTask }}
                style={({ hovered }) => [
                  styles.addTaskButton,
                  hovered && { backgroundColor: colors.solid_color_tertiary },
                  !canAddTask && { opacity: 0.5 },
                  Platform.OS === 'web' && { cursor: canAddTask ? 'pointer' : 'default' },
                ]}
                accessibilityRole="button"
                accessibilityLabel={t('chat.sourcesOutputs.addTask')}
              >
                <Icon name="plus" size={11} color={colors.text_color_secondary} />
                <Text size="s" color="secondary">
                  {t('chat.sourcesOutputs.addTask')}
                </Text>
              </Pressable>
            </View>
          </View>
        )}
        {/* Media studio and the agent's browser stay pinned so a long
            Outputs list never buries the way back into those surfaces. */}
        {!!videoStudio && (
          <View
            style={[
              styles.section,
              styles.studioSection,
              (hasInputs || hasOutputs || hasSources || hasTasks) && styles.sectionSpaced,
            ]}
          >
            <Text size="xs" color="secondary" style={styles.sectionTitle}>
              {studioTitle}
            </Text>
            <View style={styles.sectionBody}>
              <Pressable
                onPress={videoStudio.onOpen}
                accessibilityRole="button"
                accessibilityLabel={studioAction}
                style={({ hovered }) => [
                  styles.studioTile,
                  {
                    borderColor: hovered ? colors.border_color_primary : colors.border_color_secondary,
                    backgroundColor: colors.solid_color_tertiary,
                  },
                  Platform.OS === 'web' && { cursor: 'pointer' },
                ]}
              >
                <Icon name={videoStudio.kind === 'image' ? 'image' : 'clapperboard'} size={14} color={colors.text_color_tertiary} />
                <Text size="xxs" color="tertiary">{studioAction}</Text>
              </Pressable>
            </View>
          </View>
        )}
        {!!cloudBrowser && (
          <View
            style={[
              styles.section,
              styles.browserSection,
              (hasInputs || hasOutputs || hasSources || hasTasks || videoStudio) && styles.sectionSpaced,
            ]}
          >
            <Text size="xs" color="secondary" style={styles.sectionTitle}>
              {t('chat.sourcesOutputs.cloudBrowser')}
            </Text>
            <View style={styles.sectionBody}>
              <WorkBrowserPreview
                taskId={cloudBrowser.taskId}
                onOpen={cloudBrowser.onOpen}
                isLive={cloudBrowser.isLive}
              />
            </View>
          </View>
        )}
      </View>

      {/* Rendered outside the card: the panel clips its scrolling content, so
          an in-list dropdown would be cut off by the rounded container. */}
      {addMenuOpen && (
        <View
          style={[
            styles.addMenu,
            sourceActionBottom != null && { top: sourceActionBottom },
            {
              backgroundColor: colors.solid_color_primary,
              borderColor: colors.border_color_secondary,
            },
          ]}
        >
          <Pressable
            onPress={handleUploadClick}
            style={({ hovered }) => [
              styles.addMenuRow,
              hovered && { backgroundColor: colors.solid_color_tertiary },
              Platform.OS === 'web' && { cursor: 'pointer' },
            ]}
          >
            <Icon name="cloud-arrow-up" size={12} color={colors.text_color_primary} />
            <Text size="s">{t('chat.sourcesOutputs.uploadFile')}</Text>
          </Pressable>
          <Pressable
            onPress={handleLibraryClick}
            style={({ hovered }) => [
              styles.addMenuRow,
              hovered && { backgroundColor: colors.solid_color_tertiary },
              Platform.OS === 'web' && { cursor: 'pointer' },
            ]}
          >
            <Icon name="folder-open" size={12} color={colors.text_color_primary} />
            <Text size="s">{t('chat.sourcesOutputs.fromLibrary')}</Text>
          </Pressable>
        </View>
      )}

      {Platform.OS === 'web' && (
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />
      )}
    </View>
  );
};

SourcesOutputsPanel.propTypes = {
  conversationId: PropTypes.string,
  taskId: PropTypes.string,
  hasProjectFolder: PropTypes.bool,
  inputs: PropTypes.array,
  outputs: PropTypes.array,
  sources: PropTypes.array,
  collectionId: PropTypes.string,
  availableHeight: PropTypes.number,
  onOccupiedHeightChange: PropTypes.func,
  cloudBrowser: PropTypes.shape({
    taskId: PropTypes.string,
    onOpen: PropTypes.func,
    isLive: PropTypes.bool,
  }),
  videoStudio: PropTypes.shape({
    kind: PropTypes.oneOf(['image', 'video']),
    onOpen: PropTypes.func,
  }),
  tasks: PropTypes.array,
  showTasks: PropTypes.bool,
  canAddTask: PropTypes.bool,
  onAddTask: PropTypes.func,
  onEditTask: PropTypes.func,
  onSetTaskEnabled: PropTypes.func,
  onDeleteTask: PropTypes.func,
};

const styles = StyleSheet.create({
  root: {
    position: 'absolute',
    top: PANEL_TOP_OFFSET,
    right: PANEL_RIGHT_OFFSET,
    zIndex: 25,
    width: PANEL_WIDTH,
    alignItems: 'stretch',
  },
  collapsedRoot: {
    position: 'absolute',
    top: PANEL_TOP_OFFSET,
    right: PANEL_RIGHT_OFFSET,
    zIndex: 25,
    alignItems: 'flex-end',
  },
  collapsedChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    minHeight: 28,
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 8,
    backgroundColor: 'transparent',
  },
  panel: {
    borderRadius: 14,
    borderWidth: 1,
    overflow: 'hidden',
  },
  header: {
    height: HEADER_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingHorizontal: 6,
  },
  hideButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    minHeight: 22,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 7,
  },
  scroll: {
    ...(Platform.OS === 'web'
      ? { overflowY: 'auto', overflowX: 'hidden' }
      : {}),
  },
  scrollFade: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: FOOTER_HEIGHT,
    height: SCROLL_FADE_HEIGHT,
  },
  footer: {
    height: FOOTER_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  scrollContent: {
    paddingTop: 2,
    paddingBottom: 12,
    paddingHorizontal: 10,
  },
  section: {
    gap: 4,
  },
  sectionSpaced: {
    marginTop: 14,
  },
  browserSection: {
    paddingHorizontal: 10,
    paddingBottom: 12,
  }, // pinned below the source list; height must stay in sync with CLOUD_BROWSER_SECTION_HEIGHT
  studioSection: {
    paddingHorizontal: 10,
    paddingBottom: 12,
  }, // pinned above the cloud-browser tile; height must stay in sync with VIDEO_STUDIO_SECTION_HEIGHT
  tasksSection: {
    paddingHorizontal: 10,
    paddingBottom: 8,
  }, // pinned under Sources; height must stay in sync with tasksSectionHeight
  taskList: {
    ...(Platform.OS === 'web'
      ? { overflowY: 'auto', overflowX: 'hidden' }
      : {}),
  },
  studioTile: {
    height: VIDEO_STUDIO_TILE_HEIGHT,
    marginHorizontal: 6,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  sectionTitle: {
    paddingHorizontal: 6,
    marginBottom: 2,
    letterSpacing: 0.2,
  },
  sectionTitleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 22,
    paddingHorizontal: 6,
    marginBottom: 2,
    borderRadius: 7,
  },
  sectionTitleText: {
    letterSpacing: 0.2,
  },
  sectionBody: {
    gap: 1,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minHeight: 34,
    paddingHorizontal: 6,
    paddingVertical: 5,
    borderRadius: 8,
  },
  rowIconSlot: {
    width: ROW_ICON_SIZE,
    height: ROW_ICON_SIZE,
    borderRadius: 5,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  emptyHint: {
    paddingHorizontal: 6,
    paddingVertical: 6,
  },
  rowLabel: {
    flex: 1,
    minWidth: 0,
    ...(Platform.OS === 'web' ? {
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
    } : {}),
  },
  thumbImage: {
    width: ROW_ICON_SIZE,
    height: ROW_ICON_SIZE,
    borderRadius: 5,
  },
  thumbIcon: {
    width: ROW_ICON_SIZE,
    height: ROW_ICON_SIZE,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  favicon: {
    width: 14,
    height: 14,
  },
  taskRow: {
    minHeight: TASK_ROW_HEIGHT,
    paddingVertical: 4,
  },
  taskMain: {
    flex: 1,
    minWidth: 0,
    gap: 1,
  },
  taskSwitch: {
    flexShrink: 0,
    marginLeft: 2,
  },
  taskDelete: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  addTaskButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minHeight: 34,
    paddingHorizontal: 6,
    paddingVertical: 5,
    borderRadius: 8,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minHeight: 32,
    paddingHorizontal: 6,
    paddingVertical: 5,
    borderRadius: 8,
  },
  addMenu: {
    position: 'absolute',
    left: 0,
    right: 0,
    // Hangs off the bottom edge of the card rather than inside its clipped
    // scroll area.
    top: '100%',
    marginTop: 6,
    borderRadius: 10,
    borderWidth: 1,
    paddingVertical: 4,
    zIndex: 2,
  },
  addMenuRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 8,
    marginHorizontal: 4,
  },
});

export default React.memo(SourcesOutputsPanel);
